import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  LogOut,
  BookOpen,
  Loader2,
  Mail,
  Plus,
  Megaphone,
  Clock,
  Sparkles,
  User,
  Users,
  Filter,
  Trash2,
  Eye,
  Send,
  Target,
  CheckCircle,
  Globe,
  Calendar,
  XCircle,
  AlertTriangle,
  PlayCircle,
  StopCircle,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import { format, formatDistanceToNow, parseISO, isBefore, isAfter } from 'date-fns';

const MESSAGE_TYPES = [
  { value: 'SYSTEM_ANNOUNCEMENT', label: 'System Announcement', icon: Megaphone, color: 'blue', description: 'Platform updates, new features' },
  { value: 'REMINDER', label: 'Reminder', icon: Clock, color: 'amber', description: 'Profile completion, action needed' },
  { value: 'OPPORTUNITY', label: 'Opportunity', icon: Sparkles, color: 'lime', description: 'New roles, featured positions' },
  { value: 'PERSONAL', label: 'Personal', icon: User, color: 'purple', description: 'Direct communication' },
];

const TARGET_TYPES = [
  { value: 'ALL', label: 'All Users', icon: Globe, description: 'Send to everyone' },
  { value: 'SEGMENT', label: 'Segment', icon: Filter, description: 'Filter by preferences' },
  { value: 'INDIVIDUAL', label: 'Individual', icon: User, description: 'Specific user' },
];

const getMessageTypeConfig = (type) => {
  const config = {
    SYSTEM_ANNOUNCEMENT: { label: 'Announcement', icon: Megaphone, badgeClass: 'bg-blue-100 text-blue-700' },
    REMINDER: { label: 'Reminder', icon: Clock, badgeClass: 'bg-amber-100 text-amber-700' },
    OPPORTUNITY: { label: 'Opportunity', icon: Sparkles, badgeClass: 'bg-lime-100 text-lime-700' },
    PERSONAL: { label: 'Personal', icon: User, badgeClass: 'bg-purple-100 text-purple-700' },
  };
  return config[type] || config.SYSTEM_ANNOUNCEMENT;
};

const getTargetTypeConfig = (type) => {
  const config = {
    ALL: { label: 'All Users', icon: Globe, badgeClass: 'bg-slate-100 text-slate-700' },
    SEGMENT: { label: 'Segment', icon: Filter, badgeClass: 'bg-indigo-100 text-indigo-700' },
    INDIVIDUAL: { label: 'Individual', icon: User, badgeClass: 'bg-rose-100 text-rose-700' },
  };
  return config[type] || config.ALL;
};

// Helper to determine message status
const getMessageStatus = (message) => {
  const now = new Date();
  
  // Check if manually deactivated
  if (message.is_active === false) {
    return { status: 'expired', label: 'Expired', icon: XCircle, badgeClass: 'bg-red-100 text-red-700' };
  }
  
  // Check if scheduled for future
  if (message.publish_at) {
    const publishDate = new Date(message.publish_at);
    if (isAfter(publishDate, now)) {
      return { status: 'scheduled', label: 'Scheduled', icon: Clock, badgeClass: 'bg-blue-100 text-blue-700' };
    }
  }
  
  // Check if expired by date
  if (message.expires_at) {
    const expiresDate = new Date(message.expires_at);
    if (isBefore(expiresDate, now)) {
      return { status: 'expired', label: 'Expired', icon: XCircle, badgeClass: 'bg-red-100 text-red-700' };
    }
  }
  
  // Active
  return { status: 'active', label: 'Active', icon: PlayCircle, badgeClass: 'bg-lime-100 text-lime-700' };
};

// Compose Message Modal
const ComposeMessageModal = ({ isOpen, onClose, onSend, prefillData, authAxios }) => {
  const [messageType, setMessageType] = useState(prefillData?.messageType || 'SYSTEM_ANNOUNCEMENT');
  const [targetType, setTargetType] = useState(prefillData?.targetType || 'ALL');
  const [title, setTitle] = useState(prefillData?.title || '');
  const [body, setBody] = useState(prefillData?.body || '');
  const [actionUrl, setActionUrl] = useState('');
  const [actionLabel, setActionLabel] = useState('');
  
  // Multi-filter segment state
  const [segmentFilters, setSegmentFilters] = useState([]);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [estimatedReach, setEstimatedReach] = useState(null);
  
  const [targetUserId, setTargetUserId] = useState(prefillData?.targetUserId || '');
  const [targetUserEmail, setTargetUserEmail] = useState(prefillData?.targetUserEmail || '');
  const [publishAt, setPublishAt] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [sending, setSending] = useState(false);

  // Filter type options
  const filterTypeOptions = [
    { value: 'function', label: 'Function Interest', key: 'by_function' },
    { value: 'industry', label: 'Industry Interest', key: 'by_industry' },
    { value: 'geography', label: 'Geography', key: 'by_geography' },
    { value: 'experience', label: 'Experience Level', key: 'by_experience' },
    { value: 'company_size', label: 'Company Size', key: 'by_company_size' },
    { value: 'status', label: 'Onboarding Status', key: 'by_status' },
  ];

  // Fetch analytics data once when modal opens
  useEffect(() => {
    const fetchAnalytics = async () => {
      if (!authAxios || !isOpen) return;
      
      setLoadingAnalytics(true);
      try {
        const response = await authAxios.get('/admin/dashboard/analytics');
        setAnalyticsData(response.data);
      } catch (error) {
        console.error('Failed to fetch analytics:', error);
      } finally {
        setLoadingAnalytics(false);
      }
    };
    
    fetchAnalytics();
  }, [authAxios, isOpen]);

  // Handle prefill data (from Analytics drill-down)
  useEffect(() => {
    if (prefillData) {
      setMessageType(prefillData.messageType || 'SYSTEM_ANNOUNCEMENT');
      setTargetType(prefillData.targetType || 'ALL');
      setTitle(prefillData.title || '');
      setBody(prefillData.body || '');
      setTargetUserId(prefillData.targetUserId || '');
      setTargetUserEmail(prefillData.targetUserEmail || '');
      
      // Handle single filter prefill (from Analytics click)
      if (prefillData.segmentFilterType && prefillData.segmentFilterValue) {
        setSegmentFilters([{
          id: Date.now(),
          type: prefillData.segmentFilterType,
          value: prefillData.segmentFilterValue
        }]);
      }
    }
  }, [prefillData]);

  // Get value options for a filter type
  const getFilterValueOptions = (filterType) => {
    if (!analyticsData) return [];
    const config = filterTypeOptions.find(f => f.value === filterType);
    if (!config) return [];
    const data = analyticsData[config.key] || [];
    return data.map(item => item.name);
  };

  // Add a new filter
  const addFilter = () => {
    // Find first unused filter type
    const usedTypes = segmentFilters.map(f => f.type);
    const availableType = filterTypeOptions.find(f => !usedTypes.includes(f.value));
    
    if (availableType) {
      setSegmentFilters([...segmentFilters, {
        id: Date.now(),
        type: availableType.value,
        value: ''
      }]);
    }
  };

  // Remove a filter
  const removeFilter = (filterId) => {
    setSegmentFilters(segmentFilters.filter(f => f.id !== filterId));
  };

  // Update a filter
  const updateFilter = (filterId, field, value) => {
    setSegmentFilters(segmentFilters.map(f => {
      if (f.id === filterId) {
        if (field === 'type') {
          return { ...f, type: value, value: '' }; // Reset value when type changes
        }
        return { ...f, [field]: value };
      }
      return f;
    }));
  };

  // Estimate reach based on filters
  useEffect(() => {
    if (targetType !== 'SEGMENT' || segmentFilters.length === 0 || !analyticsData) {
      setEstimatedReach(null);
      return;
    }

    // For now, show the count of the smallest filter (conservative estimate)
    // In production, you'd call an API to get exact count with AND logic
    let minCount = Infinity;
    for (const filter of segmentFilters) {
      if (filter.type && filter.value) {
        const config = filterTypeOptions.find(f => f.value === filter.type);
        if (config) {
          const data = analyticsData[config.key] || [];
          const item = data.find(d => d.name === filter.value);
          if (item && item.count < minCount) {
            minCount = item.count;
          }
        }
      }
    }
    
    setEstimatedReach(minCount === Infinity ? null : `≤ ${minCount} users`);
  }, [segmentFilters, targetType, analyticsData]);

  const handleSubmit = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error('Please fill in title and message body');
      return;
    }

    // Validate segment filters if SEGMENT target
    if (targetType === 'SEGMENT' && segmentFilters.length > 0) {
      const hasEmptyFilter = segmentFilters.some(f => !f.type || !f.value);
      if (hasEmptyFilter) {
        toast.error('Please complete all segment filters or remove empty ones');
        return;
      }
    }

    setSending(true);
    try {
      // Convert filters to API format
      const segmentCriteria = segmentFilters.length > 0 
        ? segmentFilters.reduce((acc, f) => {
            acc[f.type] = f.value;
            return acc;
          }, {})
        : null;

      await onSend({
        message_type: messageType,
        target_type: targetType,
        title: title.trim(),
        body: body.trim(),
        action_url: actionUrl.trim() || null,
        action_label: actionLabel.trim() || null,
        // Support both old single-filter and new multi-filter format
        segment_filter_type: targetType === 'SEGMENT' && segmentFilters.length === 1 ? segmentFilters[0].type : null,
        segment_filter_value: targetType === 'SEGMENT' && segmentFilters.length === 1 ? segmentFilters[0].value : null,
        segment_criteria: targetType === 'SEGMENT' && segmentFilters.length > 0 ? segmentCriteria : null,
        target_user_id: targetType === 'INDIVIDUAL' ? targetUserId : null,
        target_user_email: targetType === 'INDIVIDUAL' ? targetUserEmail : null,
        publish_at: publishAt ? new Date(publishAt).toISOString() : null,
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
      });
      
      // Reset form
      setTitle('');
      setBody('');
      setActionUrl('');
      setActionLabel('');
      setPublishAt('');
      setExpiresAt('');
      setSegmentFilters([]);
      onClose();
      toast.success('Message created successfully!');
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-lime-600" />
            Compose Message
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Message Type */}
          <div>
            <Label className="text-sm font-medium text-slate-700 mb-3 block">Message Type</Label>
            <div className="grid grid-cols-2 gap-2">
              {MESSAGE_TYPES.map(type => {
                const Icon = type.icon;
                const isSelected = messageType === type.value;
                return (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setMessageType(type.value)}
                    className={`p-3 rounded-lg border text-left transition-all ${
                      isSelected 
                        ? `border-${type.color}-500 bg-${type.color}-50 ring-2 ring-${type.color}-500/20` 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Icon className={`h-4 w-4 ${isSelected ? `text-${type.color}-600` : 'text-slate-400'}`} />
                      <span className={`font-medium ${isSelected ? 'text-slate-900' : 'text-slate-600'}`}>
                        {type.label}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">{type.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Target Audience */}
          <div>
            <Label className="text-sm font-medium text-slate-700 mb-3 block">Target Audience</Label>
            <div className="grid grid-cols-3 gap-2">
              {TARGET_TYPES.map(type => {
                const Icon = type.icon;
                const isSelected = targetType === type.value;
                return (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setTargetType(type.value)}
                    className={`p-3 rounded-lg border text-center transition-all ${
                      isSelected 
                        ? 'border-lime-500 bg-lime-50 ring-2 ring-lime-500/20' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <Icon className={`h-5 w-5 mx-auto ${isSelected ? 'text-lime-600' : 'text-slate-400'}`} />
                    <span className={`text-sm font-medium block mt-1 ${isSelected ? 'text-slate-900' : 'text-slate-600'}`}>
                      {type.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Segment Filter (if SEGMENT selected) */}
          {targetType === 'SEGMENT' && (
            <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-sm font-medium text-indigo-700">Segment Criteria</Label>
                {segmentFilters.length < filterTypeOptions.length && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addFilter}
                    className="text-indigo-600 border-indigo-300 hover:bg-indigo-100"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Filter
                  </Button>
                )}
              </div>
              
              {loadingAnalytics ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-indigo-500" />
                  <span className="ml-2 text-sm text-indigo-600">Loading filter options...</span>
                </div>
              ) : segmentFilters.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-sm text-indigo-600 mb-2">No filters added yet</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addFilter}
                    className="text-indigo-600 border-indigo-300 hover:bg-indigo-100"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add First Filter
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {segmentFilters.map((filter, index) => {
                    const usedTypes = segmentFilters.filter(f => f.id !== filter.id).map(f => f.type);
                    const availableTypes = filterTypeOptions.filter(f => !usedTypes.includes(f.value) || f.value === filter.type);
                    const valueOptions = getFilterValueOptions(filter.type);
                    
                    return (
                      <div key={filter.id} className="flex items-start gap-2 p-3 bg-white rounded-lg border border-indigo-100">
                        {index > 0 && (
                          <span className="text-xs font-medium text-indigo-500 bg-indigo-100 px-2 py-1 rounded mt-1">AND</span>
                        )}
                        <div className="flex-1 grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs text-slate-500">Filter By</Label>
                            <Select 
                              value={filter.type} 
                              onValueChange={(val) => updateFilter(filter.id, 'type', val)}
                            >
                              <SelectTrigger className="h-9">
                                <SelectValue placeholder="Select type" />
                              </SelectTrigger>
                              <SelectContent>
                                {availableTypes.map(opt => (
                                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs text-slate-500">Value</Label>
                            {valueOptions.length > 0 ? (
                              <Select 
                                value={filter.value} 
                                onValueChange={(val) => updateFilter(filter.id, 'value', val)}
                              >
                                <SelectTrigger className="h-9">
                                  <SelectValue placeholder="Select value" />
                                </SelectTrigger>
                                <SelectContent>
                                  {valueOptions.map(opt => (
                                    <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <Input 
                                className="h-9"
                                placeholder={filter.type ? "Enter value" : "Select type first"}
                                value={filter.value}
                                onChange={(e) => updateFilter(filter.id, 'value', e.target.value)}
                                disabled={!filter.type}
                              />
                            )}
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFilter(filter.id)}
                          className="text-slate-400 hover:text-red-500 mt-5"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
              
              {/* Summary of filters */}
              {segmentFilters.length > 0 && segmentFilters.every(f => f.type && f.value) && (
                <div className="mt-3 p-3 bg-indigo-100 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-indigo-800 flex items-center gap-1">
                        <Target className="h-4 w-4" />
                        Targeting users matching:
                      </p>
                      <div className="text-sm text-indigo-700 mt-1">
                        {segmentFilters.map((f, i) => (
                          <span key={f.id}>
                            {i > 0 && <span className="font-medium"> AND </span>}
                            <span className="font-medium">{filterTypeOptions.find(o => o.value === f.type)?.label}</span>
                            {' = '}
                            <span className="font-semibold">&quot;{f.value}&quot;</span>
                          </span>
                        ))}
                      </div>
                    </div>
                    {estimatedReach && (
                      <Badge variant="secondary" className="bg-indigo-200 text-indigo-800">
                        {estimatedReach}
                      </Badge>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Individual Target (if INDIVIDUAL selected) */}
          {targetType === 'INDIVIDUAL' && (
            <div className="p-4 bg-rose-50 rounded-lg border border-rose-200">
              <Label className="text-sm font-medium text-rose-700 mb-2 block">Target User</Label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">User ID</Label>
                  <Input 
                    placeholder="User ID"
                    value={targetUserId}
                    onChange={(e) => setTargetUserId(e.target.value)}
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Email (for reference)</Label>
                  <Input 
                    placeholder="user@example.com"
                    value={targetUserEmail}
                    onChange={(e) => setTargetUserEmail(e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Message Content */}
          <div>
            <Label className="text-sm font-medium text-slate-700 mb-2 block">Title</Label>
            <Input 
              placeholder="Enter message title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div>
            <Label className="text-sm font-medium text-slate-700 mb-2 block">Message Body</Label>
            <Textarea 
              placeholder="Enter your message..."
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={4}
            />
          </div>

          {/* Action Button (Optional) */}
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
            <Label className="text-sm font-medium text-slate-700 mb-2 block">
              Action Button <span className="text-slate-400 font-normal">(optional)</span>
            </Label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-500">Button Label</Label>
                <Input 
                  placeholder="e.g., Update Profile"
                  value={actionLabel}
                  onChange={(e) => setActionLabel(e.target.value)}
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Button URL</Label>
                <Input 
                  placeholder="e.g., /onboarding or https://..."
                  value={actionUrl}
                  onChange={(e) => setActionUrl(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Scheduling Options */}
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <Label className="text-sm font-medium text-blue-700 mb-2 flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Schedule Message <span className="text-blue-400 font-normal">(optional)</span>
            </Label>
            <p className="text-xs text-blue-600 mb-3">
              Leave empty to publish immediately. Set dates to schedule when the message becomes visible and when it auto-expires.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-blue-600">Publish At</Label>
                <Input 
                  type="datetime-local"
                  value={publishAt}
                  onChange={(e) => setPublishAt(e.target.value)}
                  className="bg-white"
                />
                <p className="text-xs text-blue-500 mt-1">When should this go live?</p>
              </div>
              <div>
                <Label className="text-xs text-blue-600">Expires At</Label>
                <Input 
                  type="datetime-local"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                  className="bg-white"
                />
                <p className="text-xs text-blue-500 mt-1">When should this auto-hide?</p>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            disabled={sending}
            className="bg-evergreen-950 hover:bg-evergreen-900"
          >
            {sending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Send className="h-4 w-4 mr-2" />
            )}
            {publishAt ? 'Schedule Message' : 'Send Message'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Message Detail Modal
const MessageDetailModal = ({ message, isOpen, onClose, onDelete, onExpire }) => {
  if (!message) return null;
  
  const typeConfig = getMessageTypeConfig(message.message_type);
  const targetConfig = getTargetTypeConfig(message.target_type);
  const statusConfig = getMessageStatus(message);
  const TypeIcon = typeConfig.icon;
  const TargetIcon = targetConfig.icon;
  const StatusIcon = statusConfig.icon;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <Badge className={statusConfig.badgeClass}>
              <StatusIcon className="h-3 w-3 mr-1" />
              {statusConfig.label}
            </Badge>
            <Badge className={typeConfig.badgeClass}>
              <TypeIcon className="h-3 w-3 mr-1" />
              {typeConfig.label}
            </Badge>
            <Badge className={targetConfig.badgeClass}>
              <TargetIcon className="h-3 w-3 mr-1" />
              {targetConfig.label}
            </Badge>
          </div>
          <DialogTitle className="text-xl">{message.title}</DialogTitle>
        </DialogHeader>

        <div className="mt-4 space-y-4">
          <p className="text-slate-600 whitespace-pre-wrap">{message.body}</p>

          {message.target_type === 'SEGMENT' && (
            <div className="p-3 bg-indigo-50 rounded-lg">
              <p className="text-sm text-indigo-700">
                <strong>Segment:</strong> {message.segment_filter_type} = &quot;{message.segment_filter_value}&quot;
              </p>
            </div>
          )}

          {message.target_type === 'INDIVIDUAL' && (
            <div className="p-3 bg-rose-50 rounded-lg">
              <p className="text-sm text-rose-700">
                <strong>Recipient:</strong> {message.target_user_email || message.target_user_id}
              </p>
            </div>
          )}

          {message.action_url && (
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600">
                <strong>Action:</strong> {message.action_label} → {message.action_url}
              </p>
            </div>
          )}

          {/* Schedule Info */}
          {(message.publish_at || message.expires_at) && (
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm font-medium text-blue-700 mb-2 flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Schedule
              </p>
              <div className="grid grid-cols-2 gap-4 text-sm">
                {message.publish_at && (
                  <div>
                    <p className="text-xs text-blue-500">Publish At</p>
                    <p className="text-blue-700">{format(new Date(message.publish_at), 'MMM d, yyyy h:mm a')}</p>
                  </div>
                )}
                {message.expires_at && (
                  <div>
                    <p className="text-xs text-blue-500">Expires At</p>
                    <p className="text-blue-700">{format(new Date(message.expires_at), 'MMM d, yyyy h:mm a')}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="grid grid-cols-3 gap-4 pt-4 border-t">
            <div>
              <p className="text-xs text-slate-400">Created</p>
              <p className="text-sm font-medium">{format(new Date(message.created_at), 'MMM d, yyyy h:mm a')}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400">Target Audience</p>
              <p className="text-sm font-medium">{message.target_count || 0} users</p>
            </div>
            <div>
              <p className="text-xs text-slate-400">Read By</p>
              <p className="text-sm font-medium">{message.read_count || 0} users</p>
            </div>
          </div>

          {message.reads && message.reads.length > 0 && (
            <div className="pt-4 border-t">
              <p className="text-sm font-medium text-slate-700 mb-2">Recent Readers</p>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {message.reads.slice(0, 10).map(reader => (
                  <div key={reader.id} className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">{reader.email}</span>
                    <span className="text-slate-400 text-xs">
                      {formatDistanceToNow(new Date(reader.read_at), { addSuffix: true })}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose}>Close</Button>
          {statusConfig.status === 'active' && (
            <Button 
              variant="outline" 
              onClick={() => onExpire(message.id)}
              className="text-red-600 border-red-200 hover:bg-red-50"
              data-testid="expire-message-detail-btn"
            >
              <StopCircle className="h-4 w-4 mr-2" />
              Instant Expire
            </Button>
          )}
          <Button variant="destructive" onClick={() => onDelete(message.id)}>
            <Trash2 className="h-4 w-4 mr-2" />
            Delete Message
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function AdminMessagesPage() {
  const { authAxios, logout } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composePrefill, setComposePrefill] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [filterType, setFilterType] = useState('all');

  // Check URL params on mount to auto-open compose with prefilled data
  useEffect(() => {
    const compose = searchParams.get('compose');
    const targetType = searchParams.get('targetType');
    const segmentFilterType = searchParams.get('segmentFilterType');
    const segmentFilterValue = searchParams.get('segmentFilterValue');
    
    if (compose === 'true') {
      const prefill = {
        targetType: targetType || 'SEGMENT',
        segmentFilterType: segmentFilterType || '',
        segmentFilterValue: segmentFilterValue ? decodeURIComponent(segmentFilterValue) : '',
      };
      setComposePrefill(prefill);
      setComposeOpen(true);
      // Clear URL params after reading
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const loadMessages = useCallback(async () => {
    try {
      const params = {};
      if (filterType !== 'all') {
        params.message_type = filterType;
      }
      const response = await authAxios.get('/admin/messages', { params });
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Failed to load messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  }, [authAxios, filterType]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  const handleSendMessage = async (messageData) => {
    await authAxios.post('/admin/messages', messageData);
    loadMessages();
  };

  const handleViewMessage = async (message) => {
    try {
      const response = await authAxios.get(`/admin/messages/${message.id}`);
      setSelectedMessage(response.data);
      setDetailOpen(true);
    } catch (error) {
      console.error('Failed to load message details:', error);
      toast.error('Failed to load message details');
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    
    try {
      await authAxios.delete(`/admin/messages/${messageId}`);
      setDetailOpen(false);
      loadMessages();
      toast.success('Message deleted');
    } catch (error) {
      console.error('Failed to delete message:', error);
      toast.error('Failed to delete message');
    }
  };

  const handleInstantExpire = async (messageId) => {
    if (!window.confirm('Are you sure you want to expire this message immediately? It will no longer be visible to candidates.')) return;
    
    try {
      await authAxios.put(`/admin/messages/${messageId}/deactivate`);
      loadMessages();
      toast.success('Message expired successfully');
    } catch (error) {
      console.error('Failed to expire message:', error);
      toast.error('Failed to expire message');
    }
  };

  const openCompose = (prefill = null) => {
    setComposePrefill(prefill);
    setComposeOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-lime-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-6">
              <Link to="/" className="text-xl font-heading text-slate-900">
                <img src="/varyynt-logo.svg" alt="Varyynt" className="h-10" />
              </Link>
              <nav className="hidden md:flex items-center gap-1">
                <Link to="/admin" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  Overview
                </Link>
                <Link to="/admin/candidates" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  Candidates
                </Link>
                <Link to="/admin/job-analytics" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  Job Market
                </Link>
                <Link to="/admin/analytics" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  Analytics
                </Link>
                <Link to="/admin/market-pulse" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  Market Pulse
                </Link>
                <Link to="/admin/messages" className="px-4 py-2 text-sm font-medium text-lime-700 bg-lime-50 rounded-lg">
                  Messages
                </Link>
                <Link to="/admin/support" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  User Support
                </Link>
                <Link to="/admin/job-import" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  Job Import
                </Link>
              </nav>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/admin/guide">
                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-slate-600">
                  <BookOpen className="h-4 w-4 mr-2" />
                  Admin Guide
                </Button>
              </Link>
              <Link to="/brand" target="_blank">
                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-slate-600">
                  Brand Guide
                </Button>
              </Link>
              <Button variant="ghost" size="sm" onClick={logout}>
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-heading text-slate-900 flex items-center gap-2">
              <Mail className="h-6 w-6 text-lime-600" />
              Message Center
            </h1>
            <p className="text-slate-500 mt-1">Send announcements, reminders, and targeted messages to candidates</p>
          </div>
          <Button onClick={() => openCompose()} className="bg-evergreen-950 hover:bg-evergreen-900">
            <Plus className="h-4 w-4 mr-2" />
            Compose Message
          </Button>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card 
            className="cursor-pointer hover:shadow-md transition-shadow border-blue-200 bg-blue-50/50"
            onClick={() => openCompose({ messageType: 'SYSTEM_ANNOUNCEMENT', targetType: 'ALL' })}
          >
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <Megaphone className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-slate-900">New Announcement</p>
                <p className="text-xs text-slate-500">Broadcast to all users</p>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="cursor-pointer hover:shadow-md transition-shadow border-amber-200 bg-amber-50/50"
            onClick={() => openCompose({ messageType: 'REMINDER', targetType: 'SEGMENT', segmentFilterType: 'status', segmentFilterValue: 'In Progress' })}
          >
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="font-medium text-slate-900">Profile Reminder</p>
                <p className="text-xs text-slate-500">To incomplete profiles</p>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="cursor-pointer hover:shadow-md transition-shadow border-lime-200 bg-lime-50/50"
            onClick={() => openCompose({ messageType: 'OPPORTUNITY', targetType: 'SEGMENT' })}
          >
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-lime-100">
                <Sparkles className="h-5 w-5 text-lime-600" />
              </div>
              <div>
                <p className="font-medium text-slate-900">Featured Role</p>
                <p className="text-xs text-slate-500">Target by interest</p>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="cursor-pointer hover:shadow-md transition-shadow border-purple-200 bg-purple-50/50"
            onClick={() => openCompose({ messageType: 'PERSONAL', targetType: 'INDIVIDUAL' })}
          >
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-100">
                <User className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="font-medium text-slate-900">Direct Message</p>
                <p className="text-xs text-slate-500">To specific user</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6">
          {[
            { value: 'all', label: 'All Messages' },
            { value: 'SYSTEM_ANNOUNCEMENT', label: 'Announcements' },
            { value: 'REMINDER', label: 'Reminders' },
            { value: 'OPPORTUNITY', label: 'Opportunities' },
            { value: 'PERSONAL', label: 'Personal' },
          ].map(filter => (
            <Button
              key={filter.value}
              variant={filterType === filter.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterType(filter.value)}
              className={filterType === filter.value ? 'bg-evergreen-950' : ''}
            >
              {filter.label}
            </Button>
          ))}
        </div>

        {/* Messages List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Sent Messages</CardTitle>
          </CardHeader>
          <CardContent>
            {messages.length === 0 ? (
              <div className="text-center py-12">
                <Mail className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">No messages yet</h3>
                <p className="text-slate-500 mb-4">Start by composing your first message to candidates</p>
                <Button onClick={() => openCompose()}>
                  <Plus className="h-4 w-4 mr-2" />
                  Compose Message
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {messages.map(message => {
                  const typeConfig = getMessageTypeConfig(message.message_type);
                  const targetConfig = getTargetTypeConfig(message.target_type);
                  const statusConfig = getMessageStatus(message);
                  const TypeIcon = typeConfig.icon;
                  const StatusIcon = statusConfig.icon;
                  
                  return (
                    <div 
                      key={message.id}
                      className={`flex items-center justify-between p-4 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer ${
                        statusConfig.status === 'expired' ? 'bg-slate-100/50 opacity-70' : 
                        statusConfig.status === 'scheduled' ? 'bg-blue-50/50' : 'bg-slate-50'
                      }`}
                      onClick={() => handleViewMessage(message)}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${typeConfig.badgeClass.replace('text-', 'bg-').replace('-700', '-100')}`}>
                          <TypeIcon className={`h-5 w-5 ${typeConfig.badgeClass.split(' ')[1]}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className={statusConfig.badgeClass}>
                              <StatusIcon className="h-3 w-3 mr-1" />
                              {statusConfig.label}
                            </Badge>
                            <Badge className={typeConfig.badgeClass}>{typeConfig.label}</Badge>
                            <Badge className={targetConfig.badgeClass}>{targetConfig.label}</Badge>
                            {message.target_type === 'SEGMENT' && message.segment_filter_value && (
                              <Badge variant="outline">{message.segment_filter_value}</Badge>
                            )}
                          </div>
                          <h3 className="font-medium text-slate-900">{message.title}</h3>
                          <p className="text-sm text-slate-500 line-clamp-1">{message.body}</p>
                          {/* Schedule info */}
                          {(message.publish_at || message.expires_at) && (
                            <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                              {message.publish_at && (
                                <span className="flex items-center gap-1">
                                  <PlayCircle className="h-3 w-3" />
                                  Publish: {format(new Date(message.publish_at), 'MMM d, h:mm a')}
                                </span>
                              )}
                              {message.expires_at && (
                                <span className="flex items-center gap-1">
                                  <StopCircle className="h-3 w-3" />
                                  Expires: {format(new Date(message.expires_at), 'MMM d, h:mm a')}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <div className="text-center">
                          <p className="font-semibold text-slate-900">{message.target_count || 0}</p>
                          <p className="text-xs text-slate-400">Recipients</p>
                        </div>
                        <div className="text-center">
                          <p className="font-semibold text-lime-600">{message.read_count || 0}</p>
                          <p className="text-xs text-slate-400">Read</p>
                        </div>
                        <div className="text-right min-w-[100px]">
                          <p className="text-slate-600">{formatDistanceToNow(new Date(message.created_at), { addSuffix: true })}</p>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); handleViewMessage(message); }}
                            data-testid={`view-message-${message.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {statusConfig.status === 'active' && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); handleInstantExpire(message.id); }}
                              title="Instant Expire"
                              data-testid={`expire-message-${message.id}`}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <StopCircle className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Compose Modal */}
      <ComposeMessageModal
        isOpen={composeOpen}
        onClose={() => setComposeOpen(false)}
        onSend={handleSendMessage}
        prefillData={composePrefill}
        authAxios={authAxios}
      />

      {/* Detail Modal */}
      <MessageDetailModal
        message={selectedMessage}
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
        onDelete={handleDeleteMessage}
        onExpire={handleInstantExpire}
      />
    </div>
  );
}
